/**
 * ReferencesHost — singleton host for the Sistema de Referencias UI (T-245).
 *
 * Mount this ONCE (e.g. in EditorPage, alongside ImageLightboxHost). It owns:
 *   - The floating REFERENCE CARD (opened by 'openReferenceCard').
 *   - The floating REFERENCES PANEL (opened by 'openReferencesPanel').
 *
 * Contract (fixed — T-246 dispatches these events; do NOT change names):
 *   Consumed (window events):
 *     'openReferenceCard'   detail { referenceId }
 *     'openReferencesPanel' detail {}
 *     'referencesUpdated'   detail {}   (re-fetch after external mutation)
 *   Emitted (window events):
 *     'referencesGoToSource' detail { reference }  (card "Ir a la fuente")
 *     'referenceCardClosed'  detail {}             (card closed → clear flash)
 *     'referencesUpdated'    detail {}             (after a successful DELETE)
 *
 * REST:
 *   GET    /documents/{documentId}/references        → { references: [...] }
 *   DELETE /documents/{documentId}/references/{id}   → { ok: true }
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Trash2, ArrowRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../lib/axios';
import ConfirmDialog from '../ConfirmDialog';
import ReferencesManager from './ReferencesManager';

/**
 * Compute the 1-based per-block number for `ref` among `refs`.
 *
 * Refs sharing the same target_anchor_text + target_sheet_id land on the same
 * block (the extension matches the FIRST textblock containing the anchor).
 * Ordered by created_at — same ordering the chips use.
 */
function refNumber(refs, ref) {
  if (!ref) return 0;
  const group = refs
    .filter(
      (r) =>
        r.target_anchor_text === ref.target_anchor_text &&
        r.target_sheet_id === ref.target_sheet_id
    )
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  const idx = group.findIndex((r) => String(r.id) === String(ref.id));
  return idx >= 0 ? idx + 1 : 0;
}

const ReferencesHost = ({ documentId, sheets, onGoToSource }) => {
  const [refs, setRefs] = useState([]);
  const [cardRef, setCardRef] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Latest refs mirror for async event listeners (avoids stale closures).
  const refsRef = useRef([]);
  refsRef.current = refs;

  // --- Data ----------------------------------------------------------------
  const fetchRefs = useCallback(async () => {
    if (!documentId) return [];
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/documents/${documentId}/references`);
      const data = r.data?.references || [];
      setRefs(data);
      return data;
    } catch (e) {
      toast.error('No se pudieron cargar las referencias.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  // Fetch on mount.
  useEffect(() => {
    fetchRefs();
  }, [fetchRefs]);

  // --- Close helpers -------------------------------------------------------
  const closeCard = useCallback(() => {
    setCardRef(null);
    // EditorPage listens for this → clearReferenceFlash().
    window.dispatchEvent(new CustomEvent('referenceCardClosed'));
  }, []);

  // --- Esc to close card ---------------------------------------------------
  useEffect(() => {
    if (!cardRef) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeCard();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cardRef, closeCard]);

  // --- Event listeners -----------------------------------------------------
  useEffect(() => {
    const onOpenCard = async (e) => {
      const referenceId = e.detail?.referenceId;
      if (referenceId == null) return;
      let ref = refsRef.current.find((r) => String(r.id) === String(referenceId));
      if (!ref) {
        const data = await fetchRefs();
        ref = data.find((r) => String(r.id) === String(referenceId));
      }
      if (ref) setCardRef(ref);
      else toast.error('Referencia no encontrada.');
    };

    const onOpenPanel = () => {
      fetchRefs();
      setPanelOpen(true);
    };

    const onUpdated = () => {
      fetchRefs();
    };

    window.addEventListener('openReferenceCard', onOpenCard);
    window.addEventListener('openReferencesPanel', onOpenPanel);
    window.addEventListener('referencesUpdated', onUpdated);
    return () => {
      window.removeEventListener('openReferenceCard', onOpenCard);
      window.removeEventListener('openReferencesPanel', onOpenPanel);
      window.removeEventListener('referencesUpdated', onUpdated);
    };
  }, [fetchRefs]);

  // --- Actions -------------------------------------------------------------
  const handleGoToSource = (ref) => {
    if (!ref) return;
    // Dual channel: prop callback AND window event (EditorPage may use either).
    if (typeof onGoToSource === 'function') onGoToSource(ref);
    window.dispatchEvent(new CustomEvent('referencesGoToSource', { detail: { reference: ref } }));
  };

  const handleDelete = async () => {
    if (!cardRef) return;
    try {
      await axiosInstance.delete(`/documents/${documentId}/references/${cardRef.id}`);
      toast.success('Referencia eliminada.');
      // EditorPage refetches on this; we also refetch locally (the listener
      // does it, but being explicit avoids races).
      window.dispatchEvent(new CustomEvent('referencesUpdated'));
      setConfirmOpen(false);
      closeCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la referencia.');
    }
  };

  const cardNum = cardRef ? refNumber(refs, cardRef) : 0;

  // T-253: la tarjeta flotante pertenece al CONTEXTO DOCUMENTO — se abre al
  // pulsar un chip (n) junto al párrafo y flota al lado del texto. El gestor
  // (tabla) NO la usa: tiene su propio panel de detalle dentro de su layout.
  // Mezclar ambos mundos era el error de T-252 (z-index y modal en caliente).
  const card = cardRef ? (
    <div
      data-testid="reference-card"
      className="fixed right-4 top-24 z-50 w-80 max-h-[70vh] overflow-y-auto glass rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4"
    >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Referencia {cardNum > 0 ? `(${cardNum})` : ''}
            </h3>
            <button
              type="button"
              onClick={closeCard}
              title="Cerrar"
              className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Source quote */}
          <blockquote
            className="italic text-sm text-slate-700 dark:text-slate-300 border-l-4 border-sky-400 pl-2 mb-3"
          >
            {cardRef.source_quote || '—'}
          </blockquote>

          {/* Reason */}
          <div className="mb-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Motivo:</span>
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {cardRef.reason || '—'}
            </p>
          </div>

          {/* Source sheet */}
          <div className="mb-4">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Origen:</span>
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {cardRef.source_sheet_name || '—'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleGoToSource(cardRef)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Ir a la fuente
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar
            </button>
          </div>
    </div>
  ) : null;

  return (
    <>
      {/* Tarjeta flotante: solo en el documento (chip (n)); nunca sobre el gestor. */}
      {!panelOpen && card}

      {/* ---- GESTOR DE REFERENCIAS (tabla + detalle propios) ---- */}
      <ReferencesManager
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        documentId={documentId}
        /* T-253: "Ir a la fuente" desde la tabla cierra el gestor (estorba para
           ver el documento), abre la tarjeta flotante de esa referencia y
           navega+resalta. Los dos contextos se encadenan en vez de solaparse. */
        onGoToSource={(row) => {
          setPanelOpen(false);
          setCardRef(row);
          handleGoToSource(row);
        }}
      />

      {/* ---- DELETE CONFIRMATION ---- */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Eliminar referencia"
        description="¿Seguro que deseas eliminar esta referencia? La acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />
    </>
  );
};

export default ReferencesHost;
